import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { StateMachineService } from '../services/api/stateMachineService';
import { ArtifactItem } from '../types/artifactItems';
import { AvailableTransition } from '../types/stateMachine';

interface UseAutomaticTransitionsOptions {
  artifactId: string;
  items: ArtifactItem[];
  pollInterval?: number; // in milliseconds, default 5 minutes
  onTransitionExecuted?: (itemId: string, transitionId: string) => void;
  enabled?: boolean;
}

/**
 * Hook to poll for and handle automatic transitions
 * Checks for automatic transitions every pollInterval and executes them
 */
export const useAutomaticTransitions = (options: UseAutomaticTransitionsOptions) => {
  const {
    artifactId,
    items,
    pollInterval = 5 * 60 * 1000, // 5 minutes default
    onTransitionExecuted,
    enabled = true,
  } = options;

  const [checking, setChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkAndExecuteAutomaticTransitions = useCallback(async () => {
    if (!enabled || !artifactId || items.length === 0) return;

    try {
      setChecking(true);

      // Check each item for automatic transitions
      const automaticTransitionPromises = items.map(async (item) => {
        try {
          const availableTransitions = await StateMachineService.getAvailableTransitions(
            artifactId,
            item.id
          );

          // Find automatic transitions that can execute
          const automaticTransitions = availableTransitions.filter(
            (transition) => (transition as any).is_automatic || (transition as any).isAutomatic
          );

          // Validate and execute automatic transitions
          for (const transition of automaticTransitions) {
            try {
              const validation = await StateMachineService.validateTransition(
                artifactId,
                item.id,
                transition.id
              );

              if (validation.can_execute) {
                // Execute the automatic transition
                await StateMachineService.executeTransition(artifactId, item.id, transition.id, {
                  reason: 'Automatic transition executed',
                });

                const transitionName = (transition as any).transition_name || transition.name;
                message.info(
                  `Automatic transition "${transitionName}" executed for item ${item.name || item.id}`
                );

                onTransitionExecuted?.(item.id, transition.id);
              }
            } catch (error) {
              console.error(
                `Failed to execute automatic transition ${transition.id} for item ${item.id}:`,
                error
              );
            }
          }
        } catch (error) {
          console.error(`Failed to check automatic transitions for item ${item.id}:`, error);
        }
      });

      await Promise.all(automaticTransitionPromises);
      setLastCheck(new Date());
    } catch (error) {
      console.error('Error checking automatic transitions:', error);
    } finally {
      setChecking(false);
    }
  }, [artifactId, items, enabled, onTransitionExecuted]);

  // Set up polling interval
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial check
    void checkAndExecuteAutomaticTransitions();

    // Set up interval
    intervalRef.current = setInterval(() => {
      void checkAndExecuteAutomaticTransitions();
    }, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, pollInterval, checkAndExecuteAutomaticTransitions]);

  // Manual trigger function
  const triggerCheck = useCallback(() => {
    void checkAndExecuteAutomaticTransitions();
  }, [checkAndExecuteAutomaticTransitions]);

  return {
    checking,
    lastCheck,
    triggerCheck,
  };
};

